import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, Navigate } from 'react-router-dom';
import './App.css';
import RotatingModelCarousel from './components/RotatingModelCarousel';
import SharedAnalysis from './SharedAnalysis';
import { useAuth } from './AuthContext';
import { supabase } from './supabase';
import DashboardWrapper from './DashboardWrapper';

// Landing Page Component
const LandingPage = ({ onLogin, onSignup, isAuthenticated }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const navigate = useNavigate();

  // Reset email signup state when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setSubmitSuccess(false);
      setEmail('');
    }
  }, [isAuthenticated]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    setSubmitError('');
    // Write to Supabase waitlist_emails table
    const { error } = await supabase
      .from('waitlist_emails')
      .insert([{ email }]);
    if (error) {
      setSubmitError('Failed to join waitlist. Please try again.');
      setIsSubmitting(false);
      return;
    }
    setSubmitSuccess(true);
    setIsSubmitting(false);
    setEmail('');
  };

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      onSignup();
    }
  };

  const handleJoinWaitlist = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      document.getElementById('waitlist-section')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Scroll animation observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-page-new">
      {/* Hero Section */}
      <div className="hero-section-new">
        <div className="hero-content-new">
          <h1 className="hero-title-new">
            Get insight into how
          </h1>
          <div className="hero-model-container">
            <RotatingModelCarousel
              models={[
                "ChatGPT",
                "Perplexity", 
                "Claude",
                "Gemini",
                "DeepSeek",
                "Grok",
                "Meta AI"
              ]}
            />
          </div>

          <h1 className="hero-title-new">
            sees your site
          </h1>
          <p className="hero-description-new">
            Reach millions of consumers who are using<br />
            AI to discover new products and brands
          </p>
          
          <div className="hero-buttons-new">
            <button onClick={handleJoinWaitlist} className="hero-btn primary-new">
              Join Waitlist
            </button>
            <button onClick={handleGetStarted} className="hero-btn secondary-new">
              Get Started
            </button>
          </div>
        </div>
      </div>

      {/* Dashboard Preview Section */}
      <div className="dashboard-preview-section">
        <div className="preview-container">
          <div className="preview-header">
            <div className="preview-nav">
              <div className="preview-logo">🔗 mesh</div>
              <div className="preview-breadcrumb">
                <span>mesh</span>
                <span className="separator">›</span>
                <span>Home</span>
              </div>
            </div>
          </div>
          
          <div className="preview-content">
            <div className="preview-sidebar">
              <div className="sidebar-item active">🏠 Home</div>
              <div className="sidebar-item">📊 Sources</div>
              <div className="sidebar-item">📈 History</div>
              <div className="sidebar-item">🎯 Niches</div>
              <div className="sidebar-item">✍️ Drafts</div>
            </div>
            
            <div className="preview-main">
              <div className="main-header">
                <h2>Home</h2>
                <div className="time-filters">
                  <span className="filter-btn active">Last 24 hours</span>
                  <span className="filter-btn">Last 7 days</span>
                  <span className="filter-btn">Last 30 days</span>
                </div>
              </div>
              
              <div className="metrics-section">
                <div className="metric-main">
                  <h3>Brand visibility</h3>
                  <p className="metric-subtitle">Percentage of AI answers about web development tools that mention mesh</p>
                  <div className="visibility-score">
                    <span className="score">89.8%</span>
                    <span className="change positive">↗ 1%</span>
                    <span className="vs">vs last week</span>
                  </div>
                  
                  {/* Example Line Graph */}
                  <div className="example-graph">
                    <svg width="100%" height="120" viewBox="0 0 300 120">
                      <defs>
                        <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3"/>
                          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0"/>
                        </linearGradient>
                      </defs>
                      <polyline
                        fill="none"
                        stroke="#3b82f6"
                        strokeWidth="2"
                        points="0,80 50,60 100,70 150,40 200,30 250,35 300,25"
                      />
                      <polygon
                        fill="url(#gradient)"
                        points="0,80 50,60 100,70 150,40 200,30 250,35 300,25 300,120 0,120"
                      />
                    </svg>
                  </div>
                </div>
                
                <div className="ranking-section">
                  <h3>Most Relevant Sources</h3>
                  <div className="ranking-item">
                    <span className="rank">1</span>
                    <span className="company">📰 TechCrunch</span>
                    <span className="percentage">12</span>
                    <span className="mentions">mentions</span>
                  </div>
                  <div className="ranking-item">
                    <span className="rank">2</span>
                    <span className="company">💻 GitHub</span>
                    <span className="percentage">8</span>
                    <span className="mentions">mentions</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Waitlist Section */}
      <div id="waitlist-section" className="waitlist-section-new">
        <div className="container">
          {submitSuccess ? (
            <div className="success-message-new">
              <div className="success-icon">✓</div>
              <h3>Welcome to the waitlist!</h3>
              <p>We'll notify you when we're ready to launch.</p>
            </div>
          ) : (
            <div className="waitlist-content">
              <h2>Ready to get started?</h2>
              <p>Join thousands of businesses optimizing for AI search</p>
              <form onSubmit={handleEmailSubmit} className="waitlist-form-new">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="email-input-new"
                  required
                />
                <button type="submit" disabled={isSubmitting} className="submit-btn-new">
                  {isSubmitting ? 'Joining...' : 'Join Waitlist'}
                </button>
              </form>
              {submitError && <div className="error-text-new">{submitError}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};



const LoginPage = ({ onLogin, onSwitchToSignup }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await onLogin(formData);
    } catch (error) {
      console.error('Login failed:', error);
      alert('Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h2 className="auth-title">Sign In</h2>
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              placeholder="Enter your email"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              placeholder="Enter your password"
              required
            />
          </div>

          <button type="submit" className="auth-submit-btn" disabled={isLoading}>
            {isLoading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div className="auth-footer">
          <p>Don't have an account? <button onClick={onSwitchToSignup} className="auth-link">Sign up</button></p>
        </div>
      </div>
    </div>
  );
};



// NavBar Component
const NavBar = ({ isAuthenticated, user, onLogin, onSignup, onLogout }) => {
  return (
    <nav className="navbar">
      <div className="nav-container">
        <div className="nav-brand">
          <Link to={isAuthenticated ? "/dashboard" : "/"} className="nav-logo">
            <span className="logo-icon">🔗</span>
            <span className="logo-text">mesh</span>
          </Link>
        </div>
        
        <div className="nav-links">
          {isAuthenticated ? (
            <>
              <Link to="/dashboard" className="nav-link">Dashboard</Link>
              <div className="user-info">
                <button onClick={onLogout} className="nav-link">Sign Out</button>
              </div>
            </>
          ) : (
            <>
              <button onClick={onLogin} className="nav-link">Sign In</button>
              <button onClick={onSignup} className="nav-link signup-btn">Sign Up</button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

// Main App Component
function App() {
  const { user, isAuthenticated, signOut, loading, signUp, signIn } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [modalFormData, setModalFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    industry: ''
  });
  const [showModalConfirmation, setShowModalConfirmation] = useState(false);
  const navigate = useNavigate();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

  const openAuthModal = (mode) => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  const closeAuthModal = () => {
    setShowAuthModal(false);
    setModalFormData({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      industry: ''
    });
  };

  const switchAuthMode = () => {
    setAuthMode(authMode === 'login' ? 'signup' : 'login');
  };

  const handleLogin = async (credentials) => {
    try {
      await signIn(credentials.email, credentials.password);
      closeAuthModal();
    } catch (error) {
      throw error;
    }
  };



  const handleModalLogin = async (e) => {
    e.preventDefault();
    try {
      await signIn(modalFormData.email, modalFormData.password);
      closeAuthModal();
      // Wait a moment for auth state to update, then navigate
      setTimeout(() => navigate('/dashboard'), 100);
    } catch (error) {
      console.error('Login error:', error);
      alert('Login failed. Please check your credentials.');
    }
  };

  const handleModalSignup = async (e) => {
    e.preventDefault();
    
    if (modalFormData.password !== modalFormData.confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    try {
      await signUp(modalFormData.email, modalFormData.password, modalFormData.name, modalFormData.industry);
      // Show confirmation for email verification flow, don't redirect yet
      setShowModalConfirmation(true);
    } catch (error) {
      console.error('Signup error:', error);
      alert('Signup failed. Please try again.');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner-large">⟳</div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="App">
      <NavBar
        isAuthenticated={isAuthenticated}
        user={user}
        onLogin={() => openAuthModal('login')}
        onSignup={() => openAuthModal('signup')}
        onLogout={handleLogout}
      />

      <Routes>
        <Route path="/" element={
          <LandingPage 
            onLogin={() => openAuthModal('login')} 
            onSignup={() => openAuthModal('signup')} 
            isAuthenticated={isAuthenticated}
          />
        } />
        
        <Route path="/login" element={
          <LoginPage onLogin={handleLogin} onSwitchToSignup={() => openAuthModal('signup')} />
        } />
        
        <Route path="/optimizer" element={
          isAuthenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <div className="page-placeholder">
              <h1>Please Sign In</h1>
              <p>You need to be signed in to access the analyzer. Analysis is now available in the Dashboard.</p>
            </div>
          )
        } />
        <Route path="/shared/:id" element={<SharedAnalysis />} />
        <Route path="/dashboard" element={
          isAuthenticated ? (
            <DashboardWrapper BACKEND_URL={BACKEND_URL} />
          ) : (
            <div className="page-placeholder">
              <h1>Please Sign In</h1>
              <p>You need to be signed in to access the dashboard.</p>
            </div>
          )
        } />
      </Routes>

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="auth-modal-overlay" onClick={closeAuthModal}>
          <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
            <button className="auth-modal-close" onClick={closeAuthModal}>×</button>
            
            {showModalConfirmation ? (
              // Simple confirmation message
              <div className="auth-modal-confirmation">
                <p className="confirmation-message">
                  We've sent a confirmation link to your email. Click the link to complete your account setup.
                </p>
                <div className="confirmation-button-container">
                  <button 
                    onClick={() => {
                      setShowModalConfirmation(false);
                      setShowAuthModal(false);
                    }}
                    className="auth-submit-btn"
                  >
                    Got it!
                  </button>
                </div>
              </div>
            ) : (
              // Regular signup/login form
              <>
                <h2 className="auth-modal-title">
                  {authMode === 'login' ? 'Sign In' : 'Create Account'}
                </h2>
                <form className="auth-form" onSubmit={authMode === 'signup' ? handleModalSignup : authMode === 'login' ? handleModalLogin : undefined}>
                  {authMode === 'signup' && (
                    <input
                      type="text"
                      className="auth-input"
                      placeholder="Full Name"
                      value={modalFormData.name}
                      onChange={(e) => setModalFormData({ ...modalFormData, name: e.target.value })}
                      required
                    />
                  )}
                  <input
                    type="email"
                    className="auth-input"
                    placeholder="Email"
                    value={modalFormData.email}
                    onChange={(e) => setModalFormData({ ...modalFormData, email: e.target.value })}
                    required
                  />
                  <input
                    type="password"
                    className="auth-input"
                    placeholder="Password"
                    value={modalFormData.password}
                    onChange={(e) => setModalFormData({ ...modalFormData, password: e.target.value })}
                    required
                  />
                  {authMode === 'signup' && (
                    <input
                      type="password"
                      className="auth-input"
                      placeholder="Confirm Password"
                      value={modalFormData.confirmPassword}
                      onChange={(e) => setModalFormData({ ...modalFormData, confirmPassword: e.target.value })}
                      required
                    />
                  )}
                  {authMode === 'signup' && (
                    <select
                      className="auth-input"
                      value={modalFormData.industry}
                      onChange={(e) => setModalFormData({ ...modalFormData, industry: e.target.value })}
                      required
                    >
                      <option value="">Select your industry/use case</option>
                      <option value="ecommerce">E-commerce & Retail</option>
                      <option value="saas">SaaS & Technology</option>
                      <option value="agency">Digital Marketing Agency</option>
                      <option value="consulting">Consulting & Professional Services</option>
                      <option value="healthcare">Healthcare & Medical</option>
                      <option value="finance">Finance & Banking</option>
                      <option value="education">Education & Training</option>
                      <option value="real_estate">Real Estate</option>
                      <option value="travel">Travel & Hospitality</option>
                      <option value="media">Media & Entertainment</option>
                      <option value="nonprofit">Non-profit & Advocacy</option>
                      <option value="manufacturing">Manufacturing & Industrial</option>
                      <option value="food_beverage">Food & Beverage</option>
                      <option value="fitness">Fitness & Wellness</option>
                      <option value="legal">Legal Services</option>
                      <option value="automotive">Automotive</option>
                      <option value="beauty">Beauty & Personal Care</option>
                      <option value="home_garden">Home & Garden</option>
                      <option value="other">Other</option>
                    </select>
                  )}
                  <button type="submit" className="auth-submit-btn">
                    {authMode === 'login' ? 'Sign In' : 'Create Account'}
                  </button>
                </form>
                <div className="auth-switch">
                  {authMode === 'login' ? (
                    <p>Don't have an account? <button className="auth-switch-btn" onClick={switchAuthMode}>Sign up</button></p>
                  ) : (
                    <p>Already have an account? <button className="auth-switch-btn" onClick={switchAuthMode}>Sign in</button></p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
